/**
 * Accept and reject for everything staged on one row.
 *
 * ONE pair per row, at the end of it — not one per changed field. An entry can
 * write several fields and they move as a unit (spec D4), so a tick beside a
 * single field implied a narrower gesture than it performed and had to explain
 * itself in a tooltip. A row-scoped pair says what it does by where it sits.
 *
 * Renders nothing when the row has nothing staged, which is what keeps the
 * column of ticks from appearing down an untouched grid.
 */
import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useDraft } from '../DraftContext';
import type { DescribedRow } from '../describedRows';
import type { FieldPath } from '../types';
import { acceptBlockedReason } from '../useDraftRate';
import { CS_DANGER_TEXT, CS_PROPOSED_TEXT } from './stagedStyles';
import { STAGED_ENTRIES_ATTR, rowSources } from './proposedChangesModel';
import ProvenanceBadge from './ProvenanceBadge';

export interface StagedRowActionsProps {
  /** Every address this row can stage a change at. */
  readonly paths: readonly FieldPath[];
}

/**
 * What the pair is called, for the accessible name.
 *
 * Every DISTINCT row label under it, not the first one. A row usually has a
 * single name — that is what makes it a row — but two call sites deliberately
 * group fields that do not share one: the header renders both policy dates in
 * one cell, and the shared-limit cell owns the limit and the components sharing
 * it. Naming the first there promised to resolve one field while the click
 * resolved both, and a screen reader has no geometry to correct that with.
 */
function resolvedNames(changes: readonly DescribedRow[]): string {
  const labels = Array.from(new Set(changes.map((change) => change.rowLabel)));
  if (labels.length <= 1) {
    return labels[0] ?? '';
  }
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

const StagedRowActions = ({ paths }: StagedRowActionsProps) => {
  const { stagedFields, rate, accept, reject } = useDraft();
  const changes = stagedFields.at(paths);

  if (changes.length === 0) {
    return null;
  }

  const entryIds = Array.from(new Set(changes.map((change) => change.entryId)));

  const rowLabel = resolvedNames(changes);

  // Accept waits for the draft to price; reject never does. Same asymmetry as
  // the banner's Accept All / Reject All, through the same helper, so a held
  // tick cannot explain itself with the wrong half of it.
  const blocked = acceptBlockedReason(rate);

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      flexShrink={0}
      borderRadius="1rem"
      {...{ [STAGED_ENTRIES_ATTR]: entryIds.join(' ') }}
    >
      {/* Who made the change, beside the pair that resolves it. Both when a
          row holds edits from each. */}
      <Box display="inline-flex" gap="0.125rem" mr="0.125rem">
        {rowSources(changes).map((source) => (
          <ProvenanceBadge key={source} source={source} />
        ))}
      </Box>
      {/* A disabled MUI button emits no events, so a Tooltip over a bare one
          never opens — and "why is this greyed out" is exactly the moment the
          explanation is wanted. The span takes the events instead. */}
      <Tooltip title={blocked ?? `Accept changes to ${rowLabel}`}>
        <span>
          <IconButton
            size="small"
            aria-label={`Accept changes to ${rowLabel}`}
            disabled={blocked !== null}
            sx={{ p: 0.375 }}
            onClick={() => accept(entryIds)}
          >
            {/* Teal, not green: green is the banner's Accept All, a
                different gesture. */}
            <CheckIcon
              sx={{
                fontSize: '1.35rem',
                color: CS_PROPOSED_TEXT,
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={`Reject changes to ${rowLabel}`}>
        <IconButton
          size="small"
          aria-label={`Reject changes to ${rowLabel}`}
          sx={{ p: 0.375 }}
          onClick={() => reject(entryIds)}
        >
          <CloseIcon
            sx={{
              fontSize: '1.35rem',
              color: CS_DANGER_TEXT,
            }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default StagedRowActions;
